import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import { hasPermission } from '@/composables/usePermission'
import { useRouter } from '@core/utils/utils'
import i18n from '@/libs/i18n'
import { ref } from '@vue/composition-api'
import useCommon from "@/views/organization/useCommon"
import useCommonTodo from '@/views/apps/todo/useCommonTodo'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import useAuditLog from '@/composables/useAuditLog'

export default function useCaseAdd() {
  const { showSuccessMessage, showErrorMessage } = useNotifications()
  const { router } = useRouter()
  const { formatDate } = useCommon()
  const { uploadImprovementFile, sendEmailCase } = useCommonTodo()
  const { createItem } = useLambda()
  const { getLastCode } = useMongoDBFunctions()
  const { createInitialAuditEntry } = useAuditLog()
  const userData = store.state?.userStore?.userData
  const caseData = ref({ stage: 'registration', dueDate: new Date() })
  const isSubmitting = ref(false)
  const collection = 'improvement'

  const create = async (caseData) => {
    if (!hasPermission('case-management', 'create')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      return
    }

    try {
      const query = {
        collection,
        client_id: userData.client.$oid
      }
      const lastCode = await getLastCode(query)

      const payload = {
        client_id: oid(userData.client.$oid),
        note: caseData.note,
        assignee: oid(caseData.assignee),
        dueDate: date(formatDate(caseData.dueDate)),
        description: caseData.description || '',
        associatedCases: caseData.associatedCases?.map(e => oid(e)) || [],
        areas: caseData.areas?.map(e => oid(e)) || [],
        company: caseData.company?.map(e => oid(e)) || [],
        businessActivity: caseData.businessActivity?.map(e => oid(e)) || [],
        process: caseData.process?.map(e => oid(e)) || [],
        crime: caseData.crime?.map(e => oid(e)) || [],
        control: caseData.control?.map(e => oid(e)) || [],
        controlResponsible: caseData.controlResponsible?.map(e => oid(e)) || [],
        proposalResponsible: oid(caseData.assignee),
        approvalResponsible: caseData.approvalResponsible ? oid(caseData.approvalResponsible) : null,
        verificationResponsible: caseData.verificationResponsible ? oid(caseData.verificationResponsible) : null,
        proposalDueDate: caseData.proposalDueDate ? date(formatDate(caseData.proposalDueDate)) : null,
        approvalDueDate: caseData.approvalDueDate ? date(formatDate(caseData.approvalDueDate)) : null,
        verificationDueDate: caseData.verificationDueDate ? date(formatDate(caseData.verificationDueDate)) : null,
        stage: 'analysis',
        lastModifiedBy: userData?.worker_id?.$oid ? oid(userData.worker_id.$oid) : null,
        createdAt: date(new Date()),
        rootCause: 100,
        code: lastCode + 1,
        linkedBehaviors: caseData.linkedBehaviors || [],
        confirmation: caseData.confirmationId ? oid(caseData.confirmationId) : null,
        history: [createInitialAuditEntry()],
      }

      const { insertedId } = await createItem({ collection, payload })

      showSuccessMessage(i18n.t('message.improvement_created'))

      const improvementId = insertedId.toString()

      // Upload document to AWS and then update the case in MongoDB with the AWS document key
      if (caseData.documentFiles?.length) {
        uploadImprovementFile(caseData.documentFiles, "documents", improvementId)
      }

      // Send email to registration responsible with the details of the case created
      caseData._id = improvementId
      caseData.stage = payload.stage
      sendEmailCase(caseData, 'add', 'assignee', 'case')
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.improvement_error'))
    } finally {
      router.push({ name: 'organization-case-list' })
    }
  }

  return {
    create,
    caseData,
    isSubmitting,
  }
}
